/**
 * UID-440 (cart follow-up) — pure preview-pricing math for
 * `CartQuoteOptionSelector`, split out of the component so the money logic
 * is unit-testable without rendering React.
 *
 * Mirrors the RFQ `previewQuoteOptionTotals` (review-domain.ts) per-line
 * rounding discipline EXACTLY: each line's UNIT price is ratio-scaled first
 * (`applyVariantRatio`, ADR-0004 decimal-string/BigInt math — never a float
 * multiply), THEN multiplied by that line's quantity, and only THEN summed
 * across lines (`addAmounts`) — never the ratio applied once to an
 * aggregate subtotal, which is a different (wrong) number than what the
 * real `/cart/lines`-style POST would round to per line.
 *
 * Edge case (spec) — a cart line with NO price (`line.price` absent/
 * malformed, e.g. a `pending` line still awaiting a quote) carries no
 * amount to scale OR to add: it is excluded from every variant's total
 * (contributing the same $0 to each, so no one variant is disadvantaged
 * relative to another by this line) and counted in `excludedLineCount` so
 * the caller can render "Excludes N item(s) priced separately." Never
 * throws — `centsFromAmount` (cart-money.ts) throws on a malformed amount,
 * so unpriced/malformed lines are filtered out BEFORE any money helper
 * sees them, rather than caught after the fact.
 */
import type { components } from '../../lib/api/schema.gen'
import type { QuoteVariant } from '../rfq/domain'
import { applyVariantRatio } from '../rfq/variant-pricing'
import { addAmounts, multiplyAmount } from './cart-money'

type LineItem = components['schemas']['LineItem']

export interface CartQuoteOptionPreview {
  /** Decimal-string preview total per variant id (ADR-0004). */
  readonly totalsByVariantId: Readonly<Record<QuoteVariant['id'], string>>
  /** Count of lines excluded from the ratio-scaled totals because they
   * carry no usable price. */
  readonly excludedLineCount: number
}

const PRICE_AMOUNT_PATTERN = /^\d+(\.\d+)?$/

function hasUsablePrice(line: LineItem): boolean {
  const amount = line.price?.amount
  return typeof amount === 'string' && PRICE_AMOUNT_PATTERN.test(amount)
}

export function computeCartQuoteOptionPreview(
  lines: readonly LineItem[],
  variants: readonly QuoteVariant[],
): CartQuoteOptionPreview {
  const pricedLines = lines.filter(hasUsablePrice)
  const excludedLineCount = lines.length - pricedLines.length

  const totalsByVariantId = Object.fromEntries(
    variants.map((variant) => [
      variant.id,
      pricedLines.length > 0
        ? addAmounts(
            pricedLines.map((line) => {
              // Guarded by `hasUsablePrice` above.
              const unitAmount = (line.price as NonNullable<LineItem['price']>).amount
              const scaledUnit = applyVariantRatio(unitAmount, variant.ratio)
              return multiplyAmount(scaledUnit, line.quantity.value)
            }),
          )
        : '0.00',
    ]),
  ) as Record<QuoteVariant['id'], string>

  return { totalsByVariantId, excludedLineCount }
}
